/**
 * The resolved string table a widget process is handed, because it cannot ask for one.
 *
 * Authorised by: `docs/specs/widgets-architecture.md` §3 law 1 — "**No user-visible string may be
 * hardcoded in Swift or Kotlin.** `packages/copy` is the only place a string lives… Therefore
 * **every string a widget draws is resolved in TypeScript, in the user's register, and written
 * into the snapshot.** Native code draws whatever text it is handed and never composes its own."
 * Behind that: CLAUDE.md's copy law, product-concept §11 (literal-language mode) and
 * implementation-brief §11, which makes "literal-language mode covers 100% of shipped strings" a
 * definition-of-done clause. A home screen is a shipped surface; a Swift `Text("Where was I?")`
 * would put one register's English somewhere the mode can never reach.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * WHY THE TABLE IS THE WHOLE FAMILY AND NOT THE KEYS SOMEBODY LISTED
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * The selection below is a PREFIX MATCH over the catalogue, deliberately, rather than a
 * hand-written array of the keys the current renderers happen to look up. A hand-picked subset is
 * how a native renderer ends up holding a key with no string behind it — and a native renderer
 * cannot compose a substitute, cannot fall back to English, and cannot report the miss. It draws a
 * blank row, on a lock screen, where a word should be, and the user concludes the app has lost
 * their work (the same failure `widget.redacted.*` is written to avoid).
 *
 * So: adding `widget.resume.somethingNew` to the catalogue puts it in every payload from the next
 * write onward, with no edit here and no edit in the bridge. The cost is a few hundred bytes of
 * `UserDefaults`; the alternative costs a silent blank on the surface with the least room to
 * explain itself.
 *
 * FOUR FAMILIES TRAVEL, and each is here because something in the payload REFERS to it by key:
 *
 *   1. `widget.*`  — everything the widget draws, plus the in-app management surface's own words
 *                    (`widget.surface.*`). The management surface has `useCopy()` and does not
 *                    need the table; they travel anyway because the rule above is a prefix, and a
 *                    prefix with a carve-out in it is a hand-picked subset wearing a disguise.
 *   2. `temp.*`    — `packages/tokens`' `WidgetTemperature.labelKey` is literally `temp.${t}`, and
 *                    its own doc comment records the contract: "native code looks up
 *                    `strings[labelKey]` and composes nothing". Without these the temperature
 *                    label is missing and design-direction §3's redundancy law fails on the one
 *                    surface where spec §3 law 3 makes it mandatory rather than preferred.
 *   3. `dept.*`    — `ShoppingLine.department` in `@carn/core` is a `DepartmentId`, never a
 *                    display name (RULINGS R22). A widget that composed "Dairy & eggs" in Swift
 *                    would be the exact violation the ids were split out to prevent.
 *   4. `capture.voiceTitle` / `capture.photoTitle` — `WidgetText`'s `unnamed` member carries a
 *                    `channel`, not words (RULINGS R27), and `untitledKeyFor(channel)` in
 *                    ./loopTitle.ts is the one answer to what an unnamed loop is called. Native
 *                    resolves the same key through the same function's rule; see
 *                    `WIDGET_UNNAMED_KEYS` below, which exists so nobody has to re-derive it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * COUNTS: RESOLVED PER COUNT, AT COMPOSITION TIME
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * Two entries in the widget family carry a `count` slot — `widget.resume.coolingRow` and
 * `widget.shopping.remaining` — and both declare `one` forms, because the linter requires them
 * (critique 5.3: without them the app says "1 items remaining", and this audience notices
 * imprecision immediately).
 *
 * There were three ways to get that right on a surface that cannot run `pick()`:
 *
 *   (a) ship the template (`"{count} left"`) and let native substitute. Native then has to know
 *       that `{count}` is a slot, that `1` selects a different template, and — the moment a
 *       second locale exists — which of that locale's plural categories applies. That is English
 *       grammar implemented twice, in two languages, in the two files nobody reviews.
 *   (b) ship both forms (`one` and `other`) and let native choose. Smaller, and still wrong for
 *       the same reason: "choose by count" IS the grammar rule, and languages disagree about it.
 *   (c) **resolve per count, here, for the counts actually in the snapshot.** The renderer does
 *       one string concatenation — `strings[key + "#" + count]` — which is a lookup, not a
 *       grammatical decision.
 *
 * (c) is what this does, and it is cheap precisely because the payload already bounds the counts:
 * a payload carries exactly one cooling count and exactly one remaining count, so the table gains
 * two entries, not two hundred. `widgetCountKey` is the one place the suffix is spelled.
 *
 * THE BARE KEY IS DELIBERATELY ABSENT for a counted entry. Emitting `widget.shopping.remaining`
 * unresolved would put the literal `{count} left` in the payload, and `fill()` leaves an
 * unmatched slot visible on purpose — so a native reader that missed the suffix rule would draw
 * "{count} left" on a home screen rather than fail visibly. A missing key draws nothing, which is
 * the honest failure and the one spec §6 already asks for.
 */

import { type CopyKey, copy } from './catalogue';
import type { Translator } from './index';
import { untitledKeyFor } from './loopTitle';
import type { CopyEntry } from './registers';

/**
 * Key → the string in the register in force. Flat, plain, `JSON.stringify`-safe.
 *
 * `Record<string, string>` rather than `Record<CopyKey, string>`: the counted entries below are
 * keyed by a SUFFIXED form that is not a catalogue key, and typing the table as if every key were
 * one would be a lie the compiler enforces.
 */
export type WidgetStringTable = Readonly<Record<string, string>>;

/**
 * The catalogue prefixes that travel in every payload. See the header for why each one is here.
 *
 * A prefix, not a list of keys — that is the whole safety property.
 */
export const WIDGET_STRING_PREFIXES = ['widget.', 'temp.', 'dept.'] as const;

/**
 * The keys `WidgetText`'s `unnamed` member can resolve to (RULINGS R27).
 *
 * Derived by asking `untitledKeyFor` rather than by restating its answer, so the widget and the
 * app can never disagree about what an unnamed voice capture is called. The channel vocabulary is
 * `CaptureChannel` in `@carn/core`, which this leaf package may not import — `untitledKeyFor`
 * takes a plain string for exactly that reason, and it is total, so probing the channels that
 * answer differently plus one representative of the fallback covers every key it can return.
 *
 * THE PROBE LIST HAS TO GROW WHEN `untitledKeyFor` GAINS A BRANCH, and that is the one weakness of
 * deriving rather than restating: a branch added there and not probed here yields a key that is
 * missing from every payload, so the widget draws the raw key on a home screen. `share-sheet` is
 * the third probe, added with the capture channel of that name (docs/DEVIATIONS.md DP9), and
 * `packages/copy/test/widgetStrings.test.ts` asserts the list covers the function's whole range.
 */
export const WIDGET_UNNAMED_KEYS: readonly CopyKey[] = [
  untitledKeyFor('photo'),
  untitledKeyFor('share-sheet'),
  untitledKeyFor('voice'),
];

/**
 * The words the iOS SHARE extension draws (product-concept §7.1's share-sheet channel;
 * docs/DEVIATIONS.md DP9).
 *
 * A HAND-PICKED LIST, LIKE `WIDGET_UNNAMED_KEYS` ABOVE AND FOR THE SAME REASON. The header's
 * "a prefix with a carve-out in it is a hand-picked subset wearing a disguise" is a rule against
 * SUBTRACTING from a travelling family, not against adding a named handful. There is no prefix
 * available here that would work: `share.*` in the catalogue is the Toss/Ask family
 * (product-concept §10 — sharing *out* of Carn), some thirty strings about people and transfers
 * that this extension has no use for and that would bloat every widget payload on the device.
 *
 * THEY TRAVEL IN THE WIDGET PAYLOAD BECAUSE THERE IS NOWHERE ELSE. An iOS app extension is a
 * separate process with its own bundle; it cannot call `useCopy()`, cannot read the settings row,
 * and cannot run JavaScript at all. The payload the app already writes into the App Group is the
 * one channel by which the user's chosen register reaches native code, and
 * `docs/specs/widgets-architecture.md` §3 law 1 already makes that the sanctioned mechanism: the
 * words are the app's, native looks a key up and composes nothing.
 *
 * `capture.saved` is REUSED rather than duplicated under a share-specific name. "Caught." is the
 * acknowledgement this product gives a capture, and a second key with the same two words would be
 * a place for the two to drift apart.
 */
export const SHARE_EXTENSION_KEYS: readonly CopyKey[] = [
  'capture.saved',
  'capture.sharedFailed',
  'capture.sharedLanded',
];

/**
 * The separator between a counted key and its count.
 *
 * `#` because it appears in no catalogue key and needs no escaping in a plist dictionary key, a
 * Kotlin map key, or a JSON object key.
 */
export const WIDGET_COUNT_SEPARATOR = '#';

/**
 * The table key for a counted string at a specific count. **The only place the suffix is spelled**
 * — Swift and Kotlin restate the rule in one line each, and this is what they restate.
 */
export function widgetCountKey(key: string, count: number): string {
  return `${key}${WIDGET_COUNT_SEPARATOR}${count}`;
}

/**
 * Does this entry select its wording from a `count` slot?
 *
 * Read through `CopyEntry` rather than off the literal type: `copy` is `as const`, so an entry
 * that declares no `slots` has no such property in its narrowed type at all, and a structural
 * probe would fail to compile on the 900-odd entries that do not.
 */
function isCounted(key: CopyKey): boolean {
  const entry: CopyEntry = copy[key];
  return entry.slots?.includes('count') === true;
}

function inFamily(key: string): boolean {
  return WIDGET_STRING_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Every key a widget — or a preview of one — can look up, resolved in the register in force.
 *
 * `t` is the translator from `makeTranslator(register)`, passed in rather than constructed here so
 * this file has no opinion about which register is in force: that is
 * `apps/mobile/src/theme/plainWords.ts`'s one answer, and a second derivation of it is exactly the
 * drift `plainWordsNow` was written to close.
 *
 * `counts` is the set of counts the payload actually carries — `ResumeSnapshot.coolingCount` and
 * `ShoppingSnapshot.remaining`. Passing none is legal and yields a table with no counted entry in
 * it, which is the right answer for a caller that has no snapshot (a test, a preview of the empty
 * state); it is NOT a licence for the bridge to skip them.
 *
 * DETERMINISTIC AND BYTE-STABLE. The keys are sorted, so two runs over the same catalogue and the
 * same register produce identical JSON — which is what lets `apps/mobile/src/native/widgets.ts`
 * decide whether to write shared storage by comparing the serialised payload, and what stops a
 * key-order flip from looking like a change (the same property `packages/tokens` protects by
 * iterating `TEMPERATURE_ORDER` rather than `Object.keys`).
 */
export function resolveWidgetStrings(
  t: Translator,
  counts: Iterable<number> = [],
): WidgetStringTable {
  const table: Record<string, string> = {};

  // De-duplicated and sorted: `WIDGET_UNNAMED_KEYS` and `SHARE_EXTENSION_KEYS` both sit outside
  // the prefixes, and a count may be repeated (an empty list and no cooling loops are both 0).
  const keys = [
    ...new Set<CopyKey>([...WIDGET_UNNAMED_KEYS, ...SHARE_EXTENSION_KEYS, ...familyKeys()]),
  ].sort();
  const wanted = [...new Set(counts)].sort((a, b) => a - b);

  for (const key of keys) {
    if (isCounted(key)) {
      // Per count, so the renderer picks by lookup rather than by grammar. See the header.
      for (const count of wanted) table[widgetCountKey(key, count)] = t(key, { count });
      continue;
    }
    table[key] = t(key);
  }

  return table;
}

/** Every catalogue key under one of the travelling prefixes. */
function familyKeys(): readonly CopyKey[] {
  return (Object.keys(copy) as CopyKey[]).filter(inFamily);
}
